/**
 * HeatingCharts V2 - Main Orchestrator Component
 *
 * Renders heating visualization charts using 15-minute interval data (V2 API).
 * This component orchestrates all V2 chart components and handles data distribution.
 *
 * Phase 1: Foundation setup - placeholder component
 * Next: Implement SummaryChart_V2, ThermostatChart_V2, etc.
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { MdArrowBack, MdArrowForward } from "react-icons/md";
import { t } from './i18n.js';
import { hasPV, isMobileLayout } from './charts/heating/heatingChartUtils';

// Import V2 chart components
import SummaryChart_V2 from './charts/heating/SummaryChart_V2';
import ThermostatChart_V2 from './charts/heating/ThermostatChart_V2';
import ConfigChart_V2 from './charts/heating/ConfigChart_V2';
import ConsumptionChart_V2 from './charts/heating/ConsumptionChart_V2';
import PowerCurrentChart_V2 from './charts/heating/PowerCurrentChart_V2';
import PowerChart_V2 from './charts/heating/PowerChart_V2';
import PieCharts_V2 from './PieCharts_V2';
import PVSummaryChart_V2 from './charts/heating/PVSummaryChart_V2';

const HeatingCharts_V2 = ({ currentDate, onPreviousDate, onNextDate, data, error, errorState }) => {
  console.log('HeatingCharts_V2: Rendering with V2 data', data);

  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [showDetails, setShowDetails] = useState(false); // Mobile: Show 15-min details

  // Determine if mobile layout
  const isMobile = useMemo(() => isMobileLayout(containerWidth), [containerWidth]);

  // Update container width
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.getBoundingClientRect().width);
      }
    };

    const timer = setTimeout(updateWidth, 100);
    window.addEventListener('resize', updateWidth);

    return () => {
      window.removeEventListener('resize', updateWidth);
      clearTimeout(timer);
    };
  }, []);

  // Error handling
  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h3>Error loading heating data</h3>
        <p>{error}</p>
        <p>Error state: {errorState}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>No heating data available for {currentDate.toLocaleDateString()}</p>
      </div>
    );
  }

  // Extract metadata
  const metadata = data.metadata || {};

  // Check if PV data is available
  const hasPVData = hasPV(data);

  // Helper to check if date is before today
  const isBeforeToday = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  return (
    <div ref={containerRef}>
      <div className="center-header">
        <h2>{t('heatingChart.title')}</h2>
      </div>

      <div className="center-text">
        <h3>
          <button onClick={onPreviousDate}><MdArrowBack /></button>
          {' '}{metadata.date}{' '}
          {isBeforeToday(currentDate) && <button onClick={onNextDate}><MdArrowForward /></button>}
        </h3>
      </div>

      {/* Mobile toggle - controls all charts */}
      {isMobile && (
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <button onClick={() => setShowDetails(!showDetails)} style={{ padding: '8px 16px', fontSize: '14px' }}>
            {showDetails ? t('heatingChart.showHourlyView') : t('heatingChart.show15minDetails')}
          </button>
        </div>
      )}

      {/* Summary Chart - includes metered overlay if available */}
      <SummaryChart_V2 data={data} metadata={metadata} isMobile={isMobile} showDetails={showDetails} />

      {/* PV Summary Chart - only shown when solar data is available */}
      {hasPVData && (
        <PVSummaryChart_V2 data={data} metadata={metadata} isMobile={isMobile} showDetails={showDetails} />
      )}

      <p></p>

      {/* Current Chart - Phase currents with fuse reference line */}
      <PowerCurrentChart_V2 data={data} metadata={metadata} isMobile={isMobile} showDetails={showDetails} />

      <p></p>

      {/* Power Chart - Separate power consumption chart */}
      <PowerChart_V2 data={data} metadata={metadata} isMobile={isMobile} showDetails={showDetails} />

      <p></p>

      {/* Pie Charts - Consumption and Cost by Device */}
      <PieCharts_V2 data={data} metadata={metadata} isMobile={isMobile} showDetails={showDetails} />

      {/* Device Details */}
      {metadata.thermostat_id_list && metadata.thermostat_id_list.length > 0 && (
        <>
          <div className="center-text">
            <h3>{t('heatingChart.thermostatDetails')}</h3>
          </div>
          {metadata.thermostat_id_list.map(thermostatId => (
            thermostatId.includes('#config') ? (
              <ConfigChart_V2
                key={thermostatId}
                data={data}
                thermostatId={thermostatId}
                metadata={metadata}
                isMobile={isMobile}
                showDetails={showDetails}
              />
            ) : (
              <ThermostatChart_V2
                key={thermostatId}
                data={data}
                thermostatId={thermostatId}
                metadata={metadata}
                isMobile={isMobile}
                showDetails={showDetails}
              />
            )
          ))}
        </>
      )}

      {/* Actor/Consumption Devices */}
      {metadata.actor_id_list && metadata.actor_id_list.length > 0 && (
        <>
          <div className="center-text">
            <h3>{t('heatingChart.consumptionDetails')}</h3>
          </div>
          {metadata.actor_id_list.map(actorId => (
            <ConsumptionChart_V2
              key={actorId}
              data={data}
              actorId={actorId}
              metadata={metadata}
              isMobile={isMobile}
              showDetails={showDetails}
            />
          ))}
        </>
      )}
    </div>
  );
};

export default HeatingCharts_V2;
